// Ported verbatim from LC2 (src/features/coreApi/residentsSlice.js). Only
// getResidentsByAccountSfid is consumed so far (MainLayout's residents fetch, feeding
// CampaignInfo's resident counts); the rest is kept for parity with future resident pages.
import {coreApiSlice} from './apiSlice';

// The paginated v2 endpoints return `{data: [...], pagination: {total, ...}}`; `pagination`
// is a sibling of `data`, so both queries pass `rawResponse: true` (see apiSlice.js) to keep
// it. The cache holds `{items, total}`: `items` accumulates across pages, `total` is the
// backend's full count for the current scope/filters (what the table footer shows).
const asResidentsPage = (response) => {
    const items = Array.isArray(response) ? response : response?.data || response?.results || [];
    return {items, total: response?.pagination?.total ?? null};
};

const mergeResidentsPage = (currentCache, newPage, {arg}) => {
    if (arg.offset === 0) return newPage;
    currentCache.items.push(...newPage.items);
    currentCache.total = newPage.total ?? currentCache.total;
};

export const residentsApi = coreApiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getResidentsByAccountSfid: builder.query({
            query: (sfid) => ({
                url: 'residents/list/',
                method: 'get',
                params: {account_id: sfid}
            }),
            providesTags: (result, error, sfid) => [{type: 'Residents', id: sfid}],
            transformResponse: (response) => {
                if (response && response.length) {
                    return response
                        .filter((resident) => resident.sfid && resident.name && resident.mobile_user_id__c)
                        .sort((a, b) => a.name.localeCompare(b.name));
                }
                return [];
            }
        }),
        // Figma: "Loyalty Cloud" file, node 18261:159418 — CRM residents grid. v2 adds
        // offset/limit pagination on top of v1's getResidentsByAccountSfid (which returns
        // everything in one call); ResidentsTable.js fetches this page-by-page as the user
        // scrolls instead of loading the full account roster up front.
        getResidentsByAccountSfidV2: builder.query({
            query: ({sfid, offset, limit, search}) => ({
                url: 'v2/residents/list/',
                method: 'get',
                params: {
                    account_id: sfid,
                    offset,
                    limit,
                    ...(search ? {[search.includes('@') ? 'email' : 'name']: search} : {})
                },
                rawResponse: true
            }),
            transformResponse: asResidentsPage,
            serializeQueryArgs: ({queryArgs}) => `${queryArgs.sfid}:${queryArgs.search || ''}`,
            merge: mergeResidentsPage,
            forceRefetch: ({currentArg, previousArg}) => currentArg?.offset !== previousArg?.offset
        }),
        // FilterPopover.js posts name/email/phone conditions here (POST body, not query
        // params, since `filters` is a list of {field, operator, value} objects) instead of
        // client-side filtering those columns over the merged getResidentsByAccountSfidV2
        // cache — everything else (community, unit, rent, lease dates, ...) still filters
        // client-side since the backend only supports these three fields today.
        getFilteredResidents: builder.query({
            query: ({sfid, offset, limit, filters}) => ({
                url: 'v2/residents/filter/',
                method: 'post',
                data: {
                    account_id: sfid,
                    offset,
                    limit,
                    filters
                },
                rawResponse: true
            }),
            transformResponse: asResidentsPage,
            serializeQueryArgs: ({queryArgs}) => `${queryArgs.sfid}:${JSON.stringify(queryArgs.filters)}`,
            merge: mergeResidentsPage,
            forceRefetch: ({currentArg, previousArg}) => currentArg?.offset !== previousArg?.offset
        }),
        updateResidentInfo: builder.mutation({
            query: ({id, dataForm, ls_user_name}) => ({
                method: 'patch',
                url: `resident/${id}/update/`,
                data: dataForm,
                params: {ls_user_name},
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
        }),
        updateResidentsPayments: builder.mutation({
            query: (data) => ({
                url: 'residents/request_payment_changes/',
                method: 'post',
                data,
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
        }),
        createResidentTransactions: builder.mutation({
            query: ({id, amount, description}) => ({
                method: 'post',
                url: `resident/${id}/transactions/`,
                data: {amount, description}
            }),
            invalidatesTags: (result, error, {id}) => (!error ? [{type: 'ResidentTransactions', id}] : [])
        }),
        getResidentTransactions: builder.query({
            query: (id) => ({
                url: `resident/${id}/transactions/all/`,
                method: 'get'
            }),
            providesTags: (result, error, id) => (!error ? [{type: 'ResidentTransactions', id}] : [])
        }),
        getResidentPaymentHistories: builder.query({
            query: (id) => ({
                url: `v2/resident_payment_histories/${id}/`,
                method: 'get'
            }),
            providesTags: (result, error, id) => (!error ? [{type: 'ResidentPaymentHistories', id}] : [])
        }),
        getExposureLeases: builder.query({
            query: ({campaignSfid, offerId}) => ({
                url: `campaigns/${campaignSfid}/offers/${offerId}/expiring-leases`,
                method: 'get'
            }),
            providesTags: [{type: 'Exposure Leases', id: 'list'}]
        })
    }),
    overrideExisting: false
});

export const {
    useLazyGetResidentsByAccountSfidQuery,
    useGetResidentsByAccountSfidQuery,
    useGetResidentsByAccountSfidV2Query,
    useGetFilteredResidentsQuery,
    useUpdateResidentInfoMutation,
    useUpdateResidentsPaymentsMutation,
    useGetResidentTransactionsQuery,
    useCreateResidentTransactionsMutation,
    useGetResidentPaymentHistoriesQuery,
    useLazyGetExposureLeasesQuery
} = residentsApi;
